/**
 * สถานะการนำทางภายในหน้าจอใบสั่งผลิต และป้ายกำกับที่ใช้ร่วมกัน
 *
 * ทำไมใช้ query string ไม่ใช่ path ย่อย: `App.tsx` ประกาศ route ของทุกโมดูลจาก
 * `DESKTOP_MODULE_ROUTES` เส้นละหนึ่ง path แบบตรงตัว และ Work Order กำหนดว่า
 * หน้าจอนี้ใช้ path ที่ลงทะเบียนไว้แล้วเส้นเดียว (`/production/orders`) ห้ามคิด
 * path ที่สองขึ้นมาเอง — เหตุผลเดียวกับ `products/productView.ts`
 */

import { isPositiveDecimal } from '../../lib/bom/types';
import { isRealCalendarDay } from '../../lib/businessTime';
import { CROSS_PLANT_SCOPE_MARKER, type PermissionsState } from '../../lib/permissions/types';
import type { StatusTone } from '../StatusBadge';

export const ORDER_ID_PARAM = 'order_id';
export const VIEW_PARAM = 'view';
/**
 * `create` ไม่ใช่ `new` เหมือนหน้าจออื่น (`products/productView.ts`,
 * `goodsReceipts/goodsReceiptView.ts`) เพราะลิงก์ "สร้างใบสั่งผลิต" จากหน้า
 * แผนการผลิต (D-127 ข้อ 4, `lib/mps/mpsView.ts: productionOrderCreateSearch`)
 * ถูก freeze ไว้ก่อนแล้วว่าใช้ `view=create` — ที่นี่ปรับให้ตรงกันแทนการเปลี่ยน
 * ฝั่งนั้นซึ่งอยู่นอก write_scope ของ `WO-P2-M5-007`
 */
export const CREATE_VIEW_VALUE = 'create';

export type ProductionOrderView =
  | { readonly kind: 'list' }
  | { readonly kind: 'detail'; readonly orderId: string }
  | { readonly kind: 'create' };

/**
 * แปลง query เป็นหน้าจอที่ต้องแสดง
 *
 * `order_id` มาก่อน `view=create` เสมอ เพื่อให้ URL ที่หลงเหลือทั้งสองค่า (เช่นหลัง
 * สร้างสำเร็จแล้วผู้ใช้กด back/forward) จบลงที่ใบที่มีอยู่จริง ไม่ใช่ฟอร์มเปล่าที่ดู
 * เหมือนงานยังไม่ถูกบันทึก
 */
export function resolveProductionOrderView(params: URLSearchParams): ProductionOrderView {
  const orderId = params.get(ORDER_ID_PARAM)?.trim() ?? '';
  if (orderId !== '') {
    return { kind: 'detail', orderId };
  }
  if (params.get(VIEW_PARAM) === CREATE_VIEW_VALUE) {
    return { kind: 'create' };
  }
  return { kind: 'list' };
}

export function orderDetailSearch(orderId: string): string {
  return `?${ORDER_ID_PARAM}=${encodeURIComponent(orderId)}`;
}

export function orderCreateSearch(): string {
  return `?${VIEW_PARAM}=${CREATE_VIEW_VALUE}`;
}

/**
 * กลับไปหน้ารายการ = path เดิมที่ไม่มี query เลย
 *
 * มีฟังก์ชันให้มันเพื่อไม่ให้ `search=""` กระจายอยู่ตาม `<Link>` หลายจุด แล้ววันที่
 * รายการมี query ของตัวเอง (เช่นจำโรงงานที่เลือกไว้) จะไม่มีใครหาเจอว่าต้องแก้ที่ไหน
 */
export function orderListSearch(): string {
  return '';
}

/* ------------------------------------------------------- ขอบเขตโรงงาน ----- */

/**
 * โรงงานที่ผู้ใช้คนนี้เลือกได้จริง — มาจาก `plant_scope` ที่เซิร์ฟเวอร์ส่งมาเท่านั้น
 *
 * `plant_code` ของกลุ่มนี้เป็น **ขอบเขตสิทธิ์** ไม่ใช่ตัวกรองความสะดวก (D-004)
 * การให้ผู้ใช้พิมพ์รหัสโรงงานเองจะได้คำขอที่รู้อยู่แล้วว่าจะถูกตอบ `403` และผู้ใช้
 * จะอ่านไม่ออกว่าพิมพ์ผิดหรือไม่มีสิทธิ์
 *
 * marker `ALL` ของ AUDITOR ถูกตัดออกจากรายการเสมอ — มันไม่ใช่รหัสโรงงาน และ
 * การปล่อยให้ไปแสดงดิบ ๆ จะทำให้ผู้ใช้อ่านว่ามีโรงงานชื่อ "ALL" อยู่จริง
 */
export function selectablePlants(state: PermissionsState): readonly string[] {
  if (state.status !== 'ready') {
    return [];
  }
  return state.permissions.plant_scope.filter(
    (code) => code.toUpperCase() !== CROSS_PLANT_SCOPE_MARKER,
  );
}

/**
 * ผู้ใช้คนนี้ถือ marker ข้ามโรงงานหรือไม่ (AUDITOR)
 *
 * marker นี้ไม่ได้แจกแจงว่ามีโรงงานใดบ้าง จึงเลือกโรงงานจากมันไม่ได้ — ที่ทำได้คือ
 * **ไม่ส่ง `plant_code`** แล้วให้เซิร์ฟเวอร์กรองตาม scope เองตามที่ contract เขียนไว้
 * ว่า `GET /v1/production-orders` "กรองตาม plant scope เสมอ"
 */
export function hasCrossPlantScope(state: PermissionsState): boolean {
  if (state.status !== 'ready') {
    return false;
  }
  return state.permissions.plant_scope.some(
    (code) => code.toUpperCase() === CROSS_PLANT_SCOPE_MARKER,
  );
}

/* ------------------------------------------------------------- ป้ายกำกับ -- */

/**
 * ป้ายสถานะภาษาไทยของ state machine ข้อ 14
 *
 * สถานะที่ไม่รู้จักคืนรหัสดิบ ไม่ใช่ "ไม่ทราบ" — ถ้าเซิร์ฟเวอร์เพิ่มสถานะใหม่
 * ผู้ใช้ควรเห็นรหัสจริงเพื่อแจ้งต่อได้ ดีกว่าเห็นคำที่ไม่มีความหมาย
 */
export function orderStatusLabel(status: string): string {
  switch (status) {
    case 'DRAFT':
      return 'ร่าง';
    case 'RELEASED':
      return 'สั่งปล่อยแล้ว';
    case 'IN_PROCESS':
      return 'กำลังผลิต';
    case 'COMPLETED':
      return 'ผลิตเสร็จ';
    case 'CLOSED':
      return 'ปิดใบแล้ว';
    case 'CANCELLED':
      return 'ยกเลิก';
    default:
      return status;
  }
}

export function orderStatusTone(status: string): StatusTone {
  switch (status) {
    case 'DRAFT':
      return 'planned';
    case 'RELEASED':
      return 'info';
    case 'IN_PROCESS':
      return 'warning';
    case 'COMPLETED':
      return 'success';
    case 'CLOSED':
      return 'neutral';
    case 'CANCELLED':
      return 'danger';
    default:
      return 'neutral';
  }
}

/* ------------------------------------------------------- prefill (D-127) -- */

/**
 * ค่าเติมล่วงหน้าของฟอร์มสร้างใบสั่งผลิต — มาจากลิงก์ "สร้างใบสั่งผลิต" ของหน้า
 * แผนการผลิต (D-127 ข้อ 4) ทุกช่องเป็นค่าเริ่มต้นที่**แก้ได้เหมือนกรอกเอง**
 * ไม่ใช่ค่าที่ถูกล็อก — ฟอร์มไม่รู้ด้วยซ้ำว่าค่านี้มาจาก URL หรือคนพิมพ์
 */
export interface ProductionOrderCreatePrefill {
  readonly plantCode: string;
  readonly productCode: string;
  readonly quantity: string;
  /** รูปแบบช่อง `datetime-local` (`YYYY-MM-DDTHH:mm`) — ว่างเมื่ออ่านไม่ออก */
  readonly startLocal: string;
  readonly endLocal: string;
}

const EMPTY_CREATE_PREFILL: ProductionOrderCreatePrefill = {
  plantCode: '',
  productCode: '',
  quantity: '',
  startLocal: '',
  endLocal: '',
};

/**
 * `YYYY-MM-DD` จริงหรือไม่ → ค่าเริ่มต้นของช่อง `datetime-local` ที่เวลา 00:00
 * ของวันนั้น (เวลาเริ่มต้นของวัน ตามที่ `ProductionOrderCreateView` ทำกับช่องวันที่
 * อยู่แล้วผ่าน `localInputToIso` → `businessMinuteToIso`) ค่าที่อ่านไม่ออกคืน `''`
 * แล้วปล่อยให้ผู้ใช้กรอกเอง — ไม่ใช่การเดา
 */
function dayParamToLocalInput(day: string | null): string {
  if (day === null) {
    return '';
  }
  const trimmed = day.trim();
  return isRealCalendarDay(trimmed) ? `${trimmed}T00:00` : '';
}

/**
 * อ่านค่าเติมล่วงหน้าจาก search param — ค่าที่ไม่ผ่านรูปแบบถูกเมินเงียบ ๆ (คืน `''`
 * ซึ่งฟอร์มอ่านเหมือนช่องที่ไม่ได้กรอก) ไม่ throw และไม่พังหน้าจอ
 *
 * `plant_code` ตรวจกับ `plants` (โรงงานที่ `plant_scope` ของผู้ใช้พามาถึงจริง) —
 * โรงงานนอกสิทธิ์ที่หลุดมาทาง URL ไม่ถูกเติม เพราะ D-004 ถือว่าโรงงานเป็นขอบเขต
 * สิทธิ์ ไม่ใช่ค่าที่ให้เดาเอาตามใจ
 */
export function resolveProductionOrderCreatePrefill(
  params: URLSearchParams,
  plants: readonly string[],
): ProductionOrderCreatePrefill {
  const plantCode = params.get('plant_code')?.trim() ?? '';
  const productCode = params.get('product_code')?.trim() ?? '';
  const quantity = params.get('planned_quantity')?.trim() ?? '';

  return {
    ...EMPTY_CREATE_PREFILL,
    plantCode: plantCode !== '' && plants.includes(plantCode) ? plantCode : '',
    productCode,
    quantity: isPositiveDecimal(quantity) ? quantity : '',
    startLocal: dayParamToLocalInput(params.get('planned_start_date')),
    endLocal: dayParamToLocalInput(params.get('planned_end_date')),
  };
}

/**
 * ตัวกรองเริ่มต้นของหน้ารายการ — มาจากลิงก์ "ดูสถานะ" ของหน้าแผนการผลิต (D-127
 * ข้อ 4) `plant_code` ถูกตรวจกับขอบเขตสิทธิ์เช่นเดียวกับฟอร์มสร้าง ค่านอกขอบเขต
 * ถูกเมิน ส่วน `product_code` ไม่มีรูปแบบให้ตรวจ (เซิร์ฟเวอร์เป็นผู้ตัดสิน) จึงรับ
 * ทุกค่าที่ไม่ว่าง
 */
export interface ProductionOrderListFilterPrefill {
  readonly plantCode: string;
  readonly productCode: string;
}

export function resolveProductionOrderListFilterPrefill(
  params: URLSearchParams,
  plants: readonly string[],
): ProductionOrderListFilterPrefill {
  const plantCode = params.get('plant_code')?.trim() ?? '';
  const productCode = params.get('product_code')?.trim() ?? '';

  return {
    plantCode: plantCode !== '' && plants.includes(plantCode) ? plantCode : '',
    productCode,
  };
}
